

import api from "../lib/api";
import { handleError } from "../lib/errors";
import { LIMITS } from "../lib/constants";


export interface Topico {
  id: string;
  titulo: string;
  ordem: number;
  ata_id: string;
}


interface BackendTopico {
  id: number;
  titulo: string;
  ordem: number;
  ata_id: number;
}


function mapBackendToFrontend(backend: BackendTopico): Topico {
  return {
    id: backend.id.toString(),
    titulo: backend.titulo,
    ordem: backend.ordem,
    ata_id: backend.ata_id.toString(),
  };
}


export async function getTopicosByAtaId(ataId: string): Promise<Topico[]> {
  try {
    const numericId = Number(ataId);
    if (isNaN(numericId)) {
      throw new Error("ID da ata inválido");
    }

    const response = await api.get(`/topicos/${numericId}`);
    const backendTopicos = Array.isArray(response.data) ? response.data : [];
    return backendTopicos.map(mapBackendToFrontend);
  } catch (error: any) {

    if (error instanceof Error) {
      const errorMessage = error.message;
      if (errorMessage.includes('ID da ata inválido') ||
          errorMessage.includes('ID do tópico inválido') ||
          errorMessage.includes('Resposta inválida do servidor')) {
        throw error;
      }
    }
    const message = handleError(error, "getTopicosByAtaId");
    throw new Error(message);
  }
}

export async function getTopicosByProjetoAteAta(projetoId: string, ataId: string): Promise<Topico[]> {
  try {
    const numericProjetoId = Number(projetoId);
    const numericAtaId = Number(ataId);
    if (isNaN(numericProjetoId) || isNaN(numericAtaId)) {
      throw new Error("ID do projeto ou ata inválido");
    }

    const response = await api.get(`/topicos/projeto/${numericProjetoId}/ate-ata/${numericAtaId}`);
    const backendTopicos = Array.isArray(response.data) ? response.data : [];
    return backendTopicos.map(mapBackendToFrontend);
  } catch (error: any) {
    if (error instanceof Error) {
      const errorMessage = error.message;
      if (errorMessage.includes('ID do projeto') ||
          errorMessage.includes('ID da ata') ||
          errorMessage.includes('Resposta inválida do servidor')) {
        throw error;
      }
    }
    const message = handleError(error, "getTopicosByProjetoAteAta");
    throw new Error(message);
  }
}


export async function createTopico(
  ataId: string,
  input: { titulo: string; ordem?: number }
): Promise<Topico> {
  try {
    const numericId = Number(ataId);
    if (isNaN(numericId)) {
      throw new Error("ID da ata inválido");
    }

    // Validação de título no nível de serviço
    if (!input.titulo || !input.titulo.trim()) {
      throw new Error('Título do tópico é obrigatório.');
    }
    if (input.titulo.trim().length > LIMITS.TOPIC_TITLE_MAX) {
      throw new Error(`Título excedeu o limite de ${LIMITS.TOPIC_TITLE_MAX} caracteres.`);
    }

    const response = await api.post(`/topicos/${numericId}`, {
      titulo: input.titulo,
      ordem: input.ordem,
    });

    if (!response.data || !response.data.id) {
      throw new Error("Resposta inválida do servidor ao criar tópico");
    }

    return mapBackendToFrontend(response.data);
  } catch (error: any) {

    if (error instanceof Error) {
      const errorMessage = error.message;
      if (errorMessage.includes('ID da ata inválido') ||
          errorMessage.includes('ID do tópico inválido') ||
          errorMessage.includes('Resposta inválida do servidor')) {
        throw error;
      }
    }
    const message = handleError(error, "createTopico");
    throw new Error(message);
  }
}


export async function updateTopico(
  topicoId: string,
  input: { titulo?: string; ordem?: number }
): Promise<Topico> {
  try {
    const numericId = Number(topicoId);
    if (isNaN(numericId)) {
      throw new Error("ID do tópico inválido");
    }

    const response = await api.put(`/topicos/${numericId}`, input);

    if (!response.data || !response.data.id) {
      throw new Error("Resposta inválida do servidor ao atualizar tópico");
    }

    return mapBackendToFrontend(response.data);
  } catch (error: any) {

    if (error instanceof Error) {
      const errorMessage = error.message;
      if (errorMessage.includes('ID da ata inválido') ||
          errorMessage.includes('ID do tópico inválido') ||
          errorMessage.includes('Resposta inválida do servidor')) {
        throw error;
      }
    }
    const message = handleError(error, "updateTopico");
    throw new Error(message);
  }
}


export async function deleteTopico(topicoId: string): Promise<void> {
  try {
    const numericId = Number(topicoId);
    if (isNaN(numericId)) {
      throw new Error("ID do tópico inválido");
    }

    await api.delete(`/topicos/${numericId}`);
  } catch (error: any) {

    if (error instanceof Error) {
      const errorMessage = error.message;
      if (errorMessage.includes('ID da ata inválido') ||
          errorMessage.includes('ID do tópico inválido')) {
        throw error;
      }
    }
    const message = handleError(error, "deleteTopico");
    throw new Error(message);
  }
}

